import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, Warning, Package, Truck, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received: { label: "Received", color: "bg-zinc-500", step: 1 },
  quoted: { label: "Quoted", color: "bg-cyan-500", step: 2 },
  approved: { label: "Approved", color: "bg-green-500", step: 3 },
  info_needed: { label: "Info Needed", color: "bg-amber-500", step: 1 },
  compounding: { label: "Compounding", color: "bg-blue-600", step: 4 },
  shipped: { label: "Shipped", color: "bg-violet-600", step: 5 },
  delivered: { label: "Delivered", color: "bg-emerald-500", step: 6 },
};

const STEPS = [
  { label: "Received", key: "received" },
  { label: "Quoted", key: "quoted" },
  { label: "Approved", key: "approved" },
  { label: "Compounding", key: "compounding" },
  { label: "Shipped", key: "shipped" },
  { label: "Delivered", key: "delivered" },
];

export default function LiveTrackingPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/active`, { withCredentials: true });
        setOrders(data.orders);
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="p-12 text-center font-mono text-sm text-zinc-500">LOADING...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="live-tracking-page">
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">LIVE TRACKING</p>
        <h1 className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950 mt-1">Active Orders</h1>
        <p className="text-sm text-zinc-500 mt-1">{orders.length} order{orders.length !== 1 ? "s" : ""} in progress</p>
      </div>

      {orders.length === 0 ? (
        <div className="border border-zinc-200 p-12 text-center">
          <CheckCircle size={40} className="text-emerald-400 mx-auto mb-3" />
          <p className="text-zinc-600 font-medium">All orders are delivered!</p>
          <Link to="/orders/new" className="text-sm text-[#0055FF] font-medium mt-2 inline-block" data-testid="new-order-from-tracking">Place a new order</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
            const currentStep = sc.step;

            return (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                className="block border border-zinc-200 hover:border-[#0055FF] transition-all duration-200 hover:-translate-y-0.5"
                data-testid={`tracking-order-${order.id}`}
              >
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-zinc-400">#{order.id?.slice(0, 8)}</span>
                      {order.is_quote_request && (
                        <span className="px-2 py-0.5 text-[10px] font-mono font-medium bg-amber-50 text-amber-700 border border-amber-200">QUOTE REQ.</span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold font-mono text-zinc-950">${order.total?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="flex items-center gap-1 mb-3">
                    {STEPS.map((step, i) => (
                      <div key={i} className="flex-1 flex items-center gap-1">
                        <div className={`h-1.5 flex-1 transition-colors ${i < currentStep ? sc.color : "bg-zinc-200"}`} />
                      </div>
                    ))}
                  </div>

                  {/* Step Labels */}
                  <div className="flex justify-between mb-3">
                    {STEPS.map((step, i) => (
                      <span key={i} className={`text-[10px] font-mono uppercase tracking-wider ${
                        i < currentStep ? "text-zinc-950 font-bold" : "text-zinc-400"
                      }`}>{step.label}</span>
                    ))}
                  </div>

                  {/* Details */}
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-zinc-600">{order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? "s" : ""}</span>
                      <span className="text-zinc-300 mx-2">|</span>
                      <span className="text-zinc-500">{order.delivery_method?.replace(/_/g, " ")}</span>
                    </div>
                    {order.eta?.message && (
                      <div className="text-right">
                        <span className="text-xs text-zinc-400">ETA: </span>
                        <span className="text-xs font-medium text-zinc-700">{order.eta.message}</span>
                      </div>
                    )}
                  </div>

                  {order.tracking_number && (
                    <div className="mt-2 px-2 py-1 bg-violet-50 border border-violet-200 inline-block">
                      <span className="text-xs font-mono text-violet-700">Tracking: {order.tracking_number}</span>
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
